/**
 * Pure state of the start screen: the form (specs/email-capture.md section 2)
 * and the board's result cell. No DOM, and only pure libs imported, so the
 * tests can drive every state.
 */
import {checkEmail, normaliseEmail} from './emailCheck.js';
import {boardLabel, LEVEL_COUNT} from './RunResult.js';
import blockedNames from '../data/blocked-names.json' with {type: 'json'};

export const NAME_MAX = 12;
// characters a name can't have: matches the check in start_run
export const NAME_REJECT = /[^\p{L}\p{N} ._'!-]/gu;

export const HINT = "We'll use it to put you on the board.";
export const GOOD = 'Work email looks good';
export const NAME_BLOCKED = 'Pick another name';

// What the server's refusals say under the email field. Only these keep the
// player on the form; anything else plays on (see playsOn).
export const SERVER_MESSAGES = {
  work_email_required: 'Use your work email',
  invalid_email: 'Enter your work email',
  name_not_allowed: NAME_BLOCKED
};

// Shown when start_run couldn't be reached and the game plays on without a
// run token. The end panel's "Couldn't save your score" is the same outcome.
export const OFFLINE_NOTICE = "Couldn't reach the leaderboard. Play on, but this score won't be saved.";
// Shown when start_run turned the run away for its rate limit (30 starts per
// email in 60 minutes). Refusing to play would only stop a real person, so
// they play on off the board, as when it can't be reached.
export const BUSY_NOTICE = "This email has started a lot of games lately. Play on, but this score won't be saved.";

/**
 * Whether a startRun result lets the game begin: a run token, or a failure
 * that isn't a refused email (no connection, the per-email rate limit). Only
 * a refused email keeps the player on the form.
 * @param {{ok: boolean, code?: string}} result from Leaderboard.startRun
 * @returns {boolean}
 */
export function playsOn(result) {
  return Boolean(result && (result.ok || result.code === 'network' || result.code === 'rate_limited'));
}

/**
 * What to tell the player about a startRun result that let the game begin.
 * Only a run without a token gets a notice.
 * @param {{ok: boolean, code?: string}} result from Leaderboard.startRun
 * @returns {string} the notice, or '' when the score will be saved
 */
export function offlineNotice(result) {
  if (!result || result.ok) {
    return '';
  }
  return result.code === 'network' ? OFFLINE_NOTICE : result.code === 'rate_limited' ? BUSY_NOTICE : '';
}

/**
 * Keep only the characters start_run accepts, so a name never quietly misses
 * the board.
 * @param {string} text
 * @returns {string}
 */
export function cleanName(text) {
  return String(text == null ? '' : text).replace(NAME_REJECT, '').slice(0, NAME_MAX);
}

const BLOCKED_WORDS = blockedNames.words;

/**
 * True when the name holds a word from data/blocked-names.json, read the way
 * start_run reads it: letters only, so "F U.C K" is caught too.
 * @param {string} name
 * @returns {boolean}
 */
export function nameBlocked(name) {
  const letters = String(name == null ? '' : name).normalize('NFKC').toUpperCase().replace(/[^A-Z]/g, '');
  return BLOCKED_WORDS.some((word) => letters.includes(word));
}

/**
 * Work out everything the form shows from what has been typed.
 * @param {Object} input
 * @param {string} input.name
 * @param {string} input.email
 * @param {string|null} input.dog picked dog id
 * @param {string|null} input.dogName its display name, upper case
 * @param {{blocked: Set<string>, allowed: Set<string>}|null} input.lists null until loaded
 * @param {boolean} [input.touched] the email field has been left once, so a
 *   half-typed address isn't called invalid mid-keystroke
 * @returns {{nameOk: boolean, emailOk: boolean, status: 'hint'|'ok'|'bad',
 *   message: string, ready: boolean, caption: string}}
 */
export function startState({name, email, dog, dogName, lists, touched = false}) {
  const blocked = nameBlocked(name);
  const nameOk = String(name || '').trim().length > 0 && !blocked;
  const typed = normaliseEmail(email);
  let emailOk = false;
  let status = 'hint';
  let message = HINT;

  if (lists && typed) {
    const result = checkEmail(typed, lists);
    if (result.ok) {
      emailOk = true;
      status = 'ok';
      message = GOOD;
    } else if (result.reason === 'blocked' || touched) {
      status = 'bad';
      message = result.message;
    }
  }

  // the name's problem first: the email may be fine
  if (blocked) {
    status = 'bad';
    message = NAME_BLOCKED;
  }

  const detailsOk = nameOk && emailOk;
  const ready = detailsOk && Boolean(dog);
  let caption = 'Add your details first';
  if (ready) {
    caption = `Ready with ${dogName || ''}`.trim();
  } else if (detailsOk) {
    caption = 'Now pick a dog';
  }
  return {nameOk, emailOk, status, message, ready, caption};
}

/**
 * The result cell of a row on the start screen's board: a star for a win,
 * L<n> for the level lost on. Rows recorded before level_reached existed have
 * it null, and show nothing rather than "Lnull".
 * @param {{won: boolean, level_reached: (number|null)}} row
 * @returns {{label: string, title: string}}
 */
export function boardResult(row) {
  if (row.won) {
    return {label: boardLabel(row), title: `Cleared all ${LEVEL_COUNT} levels`};
  }
  const level = Number(row.level_reached);
  if (row.level_reached == null || !Number.isInteger(level) || level < 1) {
    return {label: '', title: ''};
  }
  return {label: boardLabel({won: false, level_reached: level}), title: `Lost on level ${level}`};
}
